import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, Building2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useLingui } from "@lingui/react/macro";
import { useOnboarding } from "@/features/onboarding/OnboardingContext";

export const Route = createFileRoute("/(public)/onboarding/company")({
  component: CompanySetupPage,
});

function CompanySetupPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { orgName, companyNames: savedNames, setCompanyNames } = useOnboarding();

  const [names, setNames] = useState<string[]>(
    savedNames.length > 0 ? savedNames : orgName ? [orgName] : [],
  );
  const [inputValue, setInputValue] = useState("");

  const addCompany = () => {
    const name = inputValue.trim();
    if (!name) return;
    if (names.some((n) => n.toLowerCase() === name.toLowerCase())) {
      toast.error(t`Concept already added`);
      return;
    }
    setNames((prev) => [...prev, name]);
    setInputValue("");
  };

  const removeCompany = (name: string) => {
    setNames((prev) => prev.filter((n) => n !== name));
  };

  const handleContinue = () => {
    const finalNames = names.length > 0 ? names : orgName ? [orgName] : [];
    setCompanyNames(finalNames);
    navigate({ to: "/onboarding/regions" });
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h1 className="font-semibold leading-tight text-(--brand-black)" style={{ fontSize: "2rem" }}>
          {t`Set up your concepts`}
        </h1>
        <p className="text-sm leading-relaxed text-(--gray-500)">
          {t`Concepts are the brands you operate within your organization. You can create multiple concepts and assign regions and locations to each.`}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {names.map((name) => (
          <div
            key={name}
            className="flex items-center justify-between h-11 px-4 rounded-md border border-(--gray-200) bg-(--brand-white)"
          >
            <div className="flex items-center gap-2.5">
              <Building2 className="w-4 h-4 shrink-0 text-(--gray-500)" />
              <span className="text-sm font-medium text-(--brand-black)">{name}</span>
            </div>
            <button
              type="button"
              onClick={() => removeCompany(name)}
              className="w-6 h-6 flex items-center justify-center rounded transition-colors hover:bg-red-50 text-(--color-danger)"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}

        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCompany(); } }}
          placeholder={t`Enter concept name...`}
          className="w-full h-10 rounded-md px-3 text-sm outline-none transition-shadow border border-(--gray-200) text-(--brand-black) bg-(--brand-white)"
          onFocus={(e) => { e.currentTarget.style.boxShadow = "0 0 0 3px rgba(109,112,98,0.15)"; e.currentTarget.style.borderColor = "var(--gray-600)"; }}
          onBlur={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "var(--gray-200)"; }}
        />

        <button
          type="button"
          onClick={addCompany}
          className="flex items-center gap-1.5 text-sm font-medium w-fit transition-opacity hover:opacity-70 text-(--gray-600)"
        >
          <Plus className="w-4 h-4" />
          {t`Add another concept`}
        </button>
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={handleContinue}
          className="w-full h-11 flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-md transition-opacity hover:opacity-90 bg-(--brand-black)"
        >
          {t`Continue`} <ArrowRight className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => { setCompanyNames(orgName ? [orgName] : []); navigate({ to: "/onboarding/regions" }); }}
          className="text-sm font-medium text-center transition-opacity hover:opacity-70 text-(--gray-600)"
        >
          {t`Skip for now`}
        </button>
      </div>
    </div>
  );
}
